import { useRef, useState } from "react";

import styles from "@styles/forms-flow/creative.module.scss";

import {
  Button,
  Drawer,
  Label,
  Loading,
  Steps as StepsComponent,
} from "@components";

import { CreativeName } from "./components/CreativeName";
import { UploadEmailHtml } from "./components/UploadEmailHtml";
import { UrlType } from "./components/UrlType";

import CreativeDetails from "@containers/flows/CreativeDetails";
import { FlowAPI } from "@providers/FlowAPI";
import { FlowStorage } from "@storage/FlowStorage";
import { useEffect } from "react";
import { ContemplationInterval } from "./components/ContemplationInterval";
import { If } from "shared/components/control-statements";
import Coupons from "./components/Coupons";

const Steps = {
  NAME: 1,
  URL: 2,
  CONTEMPLATION: 3,
  CHOOSE_BENEFIT: 4,
  MESSAGE_EMAIL: 5,
  FINISHED: 6,
};

const options = {
  REPORT: "REPORT",
  FORM: "FORM",
};

export const EmailCreativeActionForm = ({
  visible,
  onClose,
  flowType,
  node,
  restrictions,
  onChange,
  onSave,
  isCreativeAction,
}) => {
  const [steps, setSteps] = useState(Object.keys(Steps).length);
  const [currentStep, setCurrentStep] = useState(Steps.NAME);
  const [creative, setCreative] = useState({
    creative: node?.data?.creative || {},
    coupon: node?.data?.coupon || {},
  });
  const [isValid, setIsValid] = useState(false);
  const [brandsList, setBrands] = useState([]);
  const [showStartOptions, setShowStartOptions] = useState(false);
  const [step, setStep] = useState(null);
  const [report, setReport] = useState({});
  const [maxGainEnabled, setMaxGainEnabled] = useState(false);
  const [loading, setLoading] = useState(false);

  const getFreshNode = (data) => {
    return {
      ...node,
      data: {
        ...node.data,
        ...data,
      },
    };
  };

  useEffect(() => {
    if (restrictions?.lockCreativeBenefit) {
      setSteps(
        Object.values(Steps).filter((stepIndex) =>
          [Steps.NAME, Steps.URL, Steps.MESSAGE_EMAIL].includes(stepIndex)
        ).length
      );
    }
  }, [restrictions]);

  useEffect(() => {
    const c = {
      creative: node?.data?.creative || {},
      coupon: node?.data?.coupon || {},
    };
    setCreative(c);
    setIsValid(isValidStep(currentStep, c, flowType, maxGainEnabled));
  }, [node]);

  useEffect(() => {
    setIsValid(isValidStep(currentStep, creative, flowType, maxGainEnabled));
  }, [currentStep]);

  useEffect(() => {
    if (isCreativeAction) setShowStartOptions(true);
  }, [isCreativeAction]);

  const onChangeCreative = (branch, value) => {
    const newCreative = {
      ...creative,
      [branch]: {
        ...(creative[branch] || {}),
        ...value,
      },
    };

    setCreative(newCreative);
    setIsValid(isValidStep(currentStep, newCreative, flowType, maxGainEnabled));
    onChange && onChange(getFreshNode(newCreative));
    return getFreshNode(newCreative);
  };

  useEffect(() => {
    setIsValid(isValidStep(currentStep, creative, flowType, maxGainEnabled));
  }, []);

  const nextStep = (futureCreative) => {
    const nextStep = getNextStep(
      currentStep,
      futureCreative || creative,
      restrictions,
      brandsList || []
    );
    onSave && onSave(getFreshNode(futureCreative || creative), false);

    return nextStep;
  };

  const loadReport = async (flowId, stepId, creativeId) => {
    setLoading(true);
    const response = await FlowAPI.getFlowReports(flowId, stepId);
    const allCreatives = response.getData({ creatives: [] }).creatives;
    const selectedCreative = allCreatives.filter((c) => c.id === creativeId);

    setReport(selectedCreative[0]);
    setLoading(false);
  };

  useEffect(() => {
    if (visible) {
      const stepId = node?.data?.stepId;
      const flowId = node?.data?.flow_id;
      const creativeId = node?.data?.creative?.id;

      loadReport(flowId, stepId, creativeId);
    }
  }, [node?.data?.stepId]);

  const creativeName = useRef(creative?.creative?.name);
  const startedCreative = creativeName.current;

  return (
    <Drawer
      visible={visible}
      onClose={() => {
        onClose();
        setCurrentStep(Steps.NAME);

        if (showStartOptions) setStep(null);
      }}
      showBackButton={
        (currentStep > Steps.NAME && currentStep != Steps.FINISHED) ||
        (step && showStartOptions)
      }
      backButton={() =>
        step && currentStep === Steps.NAME
          ? setStep(null)
          : setCurrentStep((prevState) =>
              getPrevStep(prevState, creative, restrictions)
            )
      }
      title={"Adicionar um criativo - Email"}
    >
      {showStartOptions && !step && !!startedCreative && (
        <>
          <Label>O que deseja fazer?</Label>

          <div className={styles.options}>
            <div
              className={styles.option}
              onClick={() => setStep(options.REPORT)}
            >
              <div className="d-flex align-center">
                <div className={styles.icon}>
                  <i className="fal fa-list" />
                </div>
                <p>Ver relatório</p>
              </div>
              <div className={styles.chevron}>
                <i className="fas fa-chevron-right" />
              </div>
            </div>
            <div
              className={styles.option}
              onClick={() => setStep(options.FORM)}
            >
              <div className="d-flex align-center">
                <div className={styles.icon}>
                  <i className="fal fa-pen-to-square" />
                </div>
                <p>Editar disparo</p>
              </div>
              <div className={styles.chevron}>
                <i className="fas fa-chevron-right" />
              </div>
            </div>
          </div>
        </>
      )}

      {step === options.REPORT && (
        <>
          <If cond={loading}>
            <div className={styles.loadingReports}>
              <Loading />
            </div>
          </If>

          <If cond={!loading}>
            <CreativeDetails
              flowType={FlowStorage.getFlowType(flowType)}
              creative={creative}
              node={node}
              report={report}
            />
          </If>
        </>
      )}

      {(((step === options.FORM || !showStartOptions) && startedCreative) ||
        !startedCreative) && (
        <>
          <StepsComponent
            steps={steps}
            stepsIsArray
            currentStep={currentStep}
          />

          <div className={styles.scrollContainer}>
            {currentStep === Steps.NAME && (
              <CreativeName
                node={node}
                flowType={flowType}
                restrictions={restrictions}
                creative={creative?.creative}
                onChangeCreative={(data) => onChangeCreative("creative", data)}
              />
            )}

            {currentStep === Steps.URL && (
              <UrlType
                creative={creative?.creative}
                onChangeCreative={(data) => {
                  onChangeCreative("creative", data);
                }}
              />
            )}

            {currentStep === Steps.CONTEMPLATION && (
              <ContemplationInterval
                creative={creative?.creative}
                onChangeCreative={(data) => {
                  onChangeCreative("creative", data);
                }}
              />
            )}

            {currentStep === Steps.CHOOSE_BENEFIT && (
              <Coupons
                creative={creative?.creative}
                onChangeCreative={(data) => {
                  onChangeCreative("creative", data);
                }}
              />
            )}

            {currentStep === Steps.MESSAGE_EMAIL && (
              <UploadEmailHtml
                coupon={creative.coupon}
                url={creative?.creative?.url}
                message={creative.creative?.message}
                restrictions={restrictions}
                onChangeCreative={(data) =>
                  onChangeCreative("creative", { message: data })
                }
              />
            )}

            {currentStep === Steps.FINISHED && (
              <div className={styles.finished}>
                <i className="far fa-check" />
                <p>Seu disparo foi configurado com sucesso!</p>
              </div>
            )}
          </div>

          <div className={styles.footer}>
            <div className="d-flex flex-column w-100">
              {currentStep !== Steps.FINISHED && (
                <>
                  {currentStep === Steps.URL && (
                    <Button
                      className="w-100 mb-15"
                      design="default-2"
                      onClick={() => {
                        const futureCreative = onChangeCreative("creative", {
                          url: {
                            type: null,
                            url: null,
                          },
                        });
                        setCurrentStep(nextStep(futureCreative));
                      }}
                    >
                      Não inserir CTA
                    </Button>
                  )}

                  <Button
                    className="w-100"
                    disabled={!isValid}
                    onClick={() => setCurrentStep(nextStep())}
                    design="primary"
                  >
                    Próximo
                  </Button>
                </>
              )}

              {currentStep === Steps.FINISHED && (
                <Button
                  className="w-100"
                  onClick={() => {
                    setCurrentStep(Steps.NAME);
                    setCreative({});
                    onClose();
                  }}
                >
                  Voltar para fluxos
                </Button>
              )}
            </div>
          </div>
        </>
      )}
    </Drawer>
  );
};

const getNextStep = (
  currentStep,
  { creative, coupon },
  restrictions,
  brandsList = []
) => {
  if (restrictions?.lockCreativeBenefit == true) {
    switch (currentStep) {
      case Steps.NAME:
        return Steps.URL;
      case Steps.URL:
        return Steps.MESSAGE_EMAIL;
      case Steps.MESSAGE_EMAIL:
        return Steps.FINISHED;
      default:
        return currentStep;
    }
  }

  switch (currentStep) {
    case Steps.NAME:
      return Steps.URL;
    case Steps.URL:
      return Steps.CONTEMPLATION;
    case Steps.CONTEMPLATION:
      return Steps.CHOOSE_BENEFIT;
    case Steps.CHOOSE_BENEFIT:
      return Steps.MESSAGE_EMAIL;
    case Steps.MESSAGE_EMAIL:
      return Steps.FINISHED;
    default:
      return currentStep;
  }
};

const getPrevStep = (
  currentStep,
  { creative, coupon },
  restrictions,
  brandsList = []
) => {
  if (restrictions?.lockCreativeBenefit == true) {
    switch (currentStep) {
      case Steps.NAME:
        return null;
      case Steps.URL:
        return Steps.NAME;
      case Steps.MESSAGE_EMAIL:
        return Steps.URL;
      default:
        return currentStep;
    }
  }

  switch (currentStep) {
    case Steps.NAME:
      return null;
    case Steps.URL:
      return Steps.NAME;
    case Steps.CONTEMPLATION:
      return Steps.URL;
    case Steps.CHOOSE_BENEFIT:
      return Steps.CONTEMPLATION;
    case Steps.MESSAGE_EMAIL:
      return Steps.CHOOSE_BENEFIT;
    default:
      return currentStep;
  }
};

const isValidStep = (currentStep, data, flowType, maxGainEnabled) => {
  const { creative, coupon } = data;

  switch (currentStep) {
    case Steps.NAME:
      return !!creative?.name && !!(flowType == "EVENT" || creative?.sendTime);
    case Steps.URL:
      return (
        creative?.url?.type == "STANDARD" ||
        (creative?.url?.type == "CUSTOM" && creative?.url?.url)
      );
    case Steps.CONTEMPLATION:
      return !!creative?.contemplation && !!creative?.contemplation?.type;
    case Steps.MESSAGE_EMAIL:
      return !!creative?.message?.subject && !!creative?.message?.filename;
  }

  return true;
};
